import React, { useEffect, useState, useRef } from "react";
import Layout from "../../common/layout";
import { Container } from "../../common/layout/styels";
import { useTranslation } from "react-i18next";
import DynamicBarChart from "../../../elements/chart";
import { BoxDiv, DashboardSection, Tile, TitleBox, Title, Count, IconWrapper } from "../dashboard/styles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getData, postData } from "../../../../backend/api";
import { FaFolderOpen, FaDownload } from "react-icons/fa";
import StackedBarChart from "../../../elements/chart/StackedBarChart";
import styled from "styled-components";
import { DateTimePicker } from "../../../elements/datepicker";
import CustomSelect from "../../../elements/select";
import Loader from "../../../elements/loader";
import moment from "moment";
import html2canvas from "html2canvas";
// Filter Bar Styles
const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: flex-end;
  padding: 15px 20px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 180px;
`;

const FilterLabel = styled.label`
  font-size: 16px;
  color: ${(props) => props.theme.lightForeground};
`;

const FilterSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  min-width: 180px;
  &:focus {
    outline: none;
    border-color: #1E40AF;
  }
`;

const DateRangeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const DateRangeInputs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  // padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  
  input {
    border: none;
    background: transparent;
    font-size: 14px;
    color: #374151;
    width: 100px;
    &:focus {
      outline: none;
    }
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${props => props.primary ? '#1E40AF' : '#fff'};
  color: ${props => props.primary ? '#fff' : '#374151'};
  border: 1px solid ${props => props.primary ? '#1E40AF' : '#e5e7eb'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.primary ? '#1e3a8a' : '#f3f4f6'};
  }
`;

const Statistics = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.themeColors);

  const [chartData, setChartData] = useState();
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Ref for capturing all charts as PNG
  const chartsRef = useRef(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    department: "",
    category: "",
    type: "",  // anonymity type
    startDate: null,
    endDate: null
  });

  const { setLoaderBox, setMessage } = props;

  useEffect(() => {
    fetchFilterOptions();
    fetchCaseChart();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      // Fetch departments
      const deptResponse = await getData({}, "department/select", dispatch, navigate);
      if (deptResponse?.data) {
        setDepartments(deptResponse.data);
      }
      
      // Fetch categories
      const catResponse = await getData({}, "whistle-category/select", dispatch, navigate);
      if (catResponse?.data) {
        setCategories(catResponse.data);
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const fetchCaseChart = async (filterParams = {}) => {
    try {
      setLoading(true);
      
      // Build query string from filters
      const queryParams = new URLSearchParams();
      if (filterParams.department) queryParams.append("department", filterParams.department);
      if (filterParams.category) queryParams.append("category", filterParams.category);
      if (filterParams.type) queryParams.append("anonymity", filterParams.type);
      if (filterParams.startDate) queryParams.append("startDate", moment(filterParams.startDate).format("YYYY-MM-DD"));
      if (filterParams.endDate) queryParams.append("endDate", moment(filterParams.endDate).format("YYYY-MM-DD"));
      
      const queryString = queryParams.toString();
      const url = queryString ? `whistleblowing/chart?${queryString}` : "whistleblowing/chart";
      
      const response = await getData({}, url, dispatch, navigate);
      if (response.data) {
        setChartData(response.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      
      // Date validation: startDate should be <= endDate
      if (field === 'startDate' && value && newFilters.endDate && value > newFilters.endDate) {
        // If startDate is after endDate, set endDate to startDate
        newFilters.endDate = moment(new Date(value)).format("YYYY-MM-DD");
      }
      if (field === 'endDate' && value && newFilters.startDate && value < newFilters.startDate) {
        // If endDate is before startDate, set startDate to endDate
        newFilters.startDate = moment(new Date(value)).format("YYYY-MM-DD");
      }
      
      return newFilters;
    });
  };

  const applyFilters = () => {
    console.log("Applying filters:", filters);
    fetchCaseChart(filters);
  };

  const resetFilters = () => {
    setFilters({
      department: "",
      category: "",
      type: "",
      startDate: null,
      endDate: null
    });
    fetchCaseChart({});
  };

  const handleExport = async () => {
    if (!chartsRef.current) {
      setMessage({ type: 2, content: t("No charts to export"), proceed: t("okay"), code: 400 });
      return;
    }
    
    // Set loading first, then allow React to render before heavy operation
    setLoading(true);
    
    // Use setTimeout to allow the loading state to render before processing
    setTimeout(async () => {
      try {
        // Capture the charts section as canvas
        const canvas = await html2canvas(chartsRef.current, {
          backgroundColor: "#ffffff",
          scale: 2, // Higher quality
          useCORS: true,
          logging: false,
        });
        
        // Convert to PNG and download
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `${t("statistics")}_${moment().format("DD-MM-YYYY_HH-mm")}.png`;
        link.click();
        
        // setMessage({ type: 1, content: t("Charts downloaded successfully"), proceed: t("okay"), code: 200 });
      } catch (error) {
        console.error("Export error:", error);
        setMessage({ type: 1, content: t("Failed to export charts"), proceed: t("okay"), code: 400});
      } finally {
        setLoading(false);
      }
    }, 100); // Small delay to allow loading state to render
  };

  // --- Chart transformations ---
  const transformCategory = () => ({
    categories: chartData?.transformCategory?.categories.map((c) => c[sessionStorage.getItem("_lang") ?? "de"]),
    series: [{ name: t("Count"), data: chartData?.transformCategory?.series[0]?.data }],
  });

  const transformTime = () => ({
    categories: chartData?.transformTime?.categories,
    series: [{ name: t("Reports"), data: chartData?.transformTime?.series[0]?.data }],
  });

  const transformChannel = () => ({
    categories: chartData?.transformChannel?.categories,
    series: [{ name: t("Count"), data: chartData?.transformChannel?.series[0]?.data }],
  });

  const transformMonthlyOverview = () => ({
    categories: chartData?.monthlyOverview?.categories || [],
    series: chartData?.monthlyOverview?.series?.map(s => ({
      name: t(s.name),
      data: s.data
    })) || [],
  });

  const transformAnnualOverview = () => ({
    categories: chartData?.annualOverview?.categories || [],
    series: chartData?.annualOverview?.series?.map(s => ({
      name: t(s.name),
      data: s.data
    })) || [],
  });

  const transformCasesCreatedPerMonth = () => ({
    categories: chartData?.casesCreatedPerMonth?.categories || [],
    series: chartData?.casesCreatedPerMonth?.series?.map(s => ({
      name: t(s.name),
      data: s.data
    })) || [],
  });

  return (
    <Container className="noshadow column">
      {!chartData && loading ? (
        <Loader />
      ) : (
        <>
      {/* Filter Bar */}
      <FilterBar>
        <FilterGroup>
          <CustomSelect
            id="department"
            name="department"
            label={t('Department')}
            value={filters.department}
            formValues={filters}
            placeholder={t('All Selected')}
            apiType="JSON"
            selectApi={departments}
            onSelect={(_, e) => handleFilterChange('department', e.id)}
          />
        </FilterGroup>

        <FilterGroup>
          <CustomSelect
            id="category"
            name="category"
            label={t('Category')}
            value={filters.category}
            formValues={filters}
            placeholder={t('All Selected')}
            apiType="JSON"
            selectApi={categories}
            onSelect={(_, e) => handleFilterChange('category', e.id)}
          >
          </CustomSelect>
        </FilterGroup>

        <FilterGroup>
          <CustomSelect
            id="type"
            name="type"
            label={t('Type')}
            value={filters.type}
            formValues={filters}
            placeholder={t('All Selected')}
            apiType="JSON"
            selectApi={[
              // { id: "", value: t("Original") },
              { id: "anonymous", value: t("Anonymous") },
              { id: "confidential", value: t("Confidential") }
            ]}
            onSelect={(_, e) => handleFilterChange('type', e.id)}
          >
          </CustomSelect>
        </FilterGroup>

        <DateRangeGroup>
          <FilterLabel theme={theme}>{t("Time period")}</FilterLabel>
          <DateRangeInputs>
            <DateTimePicker
              selected={filters.startDate}
              onChange={(date) => handleFilterChange("startDate", date)}
              dateFormat="dd.MM.yy"
              placeholderText={t("Start date")}
              maxDate={filters.endDate || new Date()}
              isClearable
            />
            <span>-</span>
            <DateTimePicker
              selected={filters.endDate}
              onChange={(date) => handleFilterChange("endDate", date)}
              dateFormat="dd.MM.yy"
              placeholderText={t("End date")}
              minDate={filters.startDate}
              maxDate={new Date()}
              isClearable
            />
          </DateRangeInputs>
        </DateRangeGroup>

        <FilterButton primary onClick={applyFilters}>
          {t("Apply")}
        </FilterButton>

        <FilterButton onClick={resetFilters}>
          {t("Reset")}
        </FilterButton>

        <FilterButton onClick={handleExport}>
          <FaDownload />
          {t("Download")}
        </FilterButton>
      </FilterBar>

      {/* Charts container for export */}
      <div ref={chartsRef}>
        {/* Number of Open Reports Card */}
        <DashboardSection count={1} style={{ marginBottom: '20px' }}>
          <Tile style={{ maxWidth: '300px' }}>
            <TitleBox>
              <Count>{chartData?.openReportsCount ?? 0}</Count>
              <Title>{t("Number of open notifications")}</Title>
            </TitleBox>
            <IconWrapper style={{ backgroundColor: '#1E40AF' }}>
              <FaFolderOpen />
            </IconWrapper>
          </Tile>
        </DashboardSection>

        {/* Charts Grid */}
        <DashboardSection count={3} className="two">
        {/* Time of Notification */}
        <BoxDiv className="radious" style={{ padding: '10px' }}>
          <StackedBarChart
            t={t}
            chartData={transformTime()}
            title="Time of notification"
          />
        </BoxDiv>

        {/* Breakdown by Reporting Channel */}
        <BoxDiv className="radious" style={{ padding: '10px' }}>
          <StackedBarChart
            t={t}
            chartData={transformChannel()}
            title="Breakdown by reporting channel"
          />
        </BoxDiv>

        {/* Monthly Overview (Last 12 Months) - Stacked */}
        <BoxDiv className="radious" style={{ padding: '10px' }}>
          <StackedBarChart
            t={t}
            chartData={transformMonthlyOverview()}
            title="Monthly overview"
          />
        </BoxDiv>

        {/* Breakdown by Category */}
        <BoxDiv className="radious" style={{ padding: '10px' }}>
          <StackedBarChart
            t={t}
            chartData={transformCategory()}
            title="Breakdown by category"
          />
        </BoxDiv>

        {/* Cases Created Per Month */}
        <BoxDiv className="radious" style={{ padding: '10px' }}>
          <StackedBarChart
            t={t}
            chartData={transformCasesCreatedPerMonth()}
            title="new reports last month"
          />
        </BoxDiv>

        {/* Annual Overview - Stacked */}
        <BoxDiv className="radious" style={{ padding: '10px' }}>
          <StackedBarChart
            t={t}
            chartData={transformAnnualOverview()}
            title="Annual overview"
          />
        </BoxDiv>
        </DashboardSection>
      </div>

      {loading && <Loader />}
      </>
      )}
    </Container>
  );
};

export default Layout(Statistics);
